import React, { createContext, useState, useContext, useEffect } from 'react';
import { getApiUrl } from '../config/api';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check Supabase session
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        if (!sessionError && sessionData?.session) {
          // Verify the user still exists by trying to get user info
          try {
            const { data: userData, error: userError } = await supabase.auth.getUser();
            
            if (userError || !userData?.user) {
              // User doesn't exist or token is invalid
              console.warn('User not found in Supabase auth, clearing session:', userError);
              await supabase.auth.signOut();
              localStorage.removeItem('scanmaster_user');
              localStorage.removeItem('scanmaster_token');
              setUser(null);
              setLoading(false);
              return;
            }
            
            // User exists and token is valid
            const supabaseUser = userData.user;
            const appUser = {
              email: supabaseUser.email || '',
              name: supabaseUser.user_metadata?.full_name || 
                    supabaseUser.user_metadata?.name || 
                    (supabaseUser.email ? supabaseUser.email.split('@')[0] : ''),
              token: sessionData.session.access_token
            };
            setUser(appUser);
            localStorage.setItem('scanmaster_user', JSON.stringify(appUser));
            localStorage.setItem('scanmaster_token', sessionData.session.access_token);
          } catch (verifyError) {
            // If getUser fails, the token is likely invalid
            console.error('Error verifying user:', verifyError);
            await supabase.auth.signOut();
            localStorage.removeItem('scanmaster_user');
            localStorage.removeItem('scanmaster_token');
            setUser(null);
            setLoading(false);
            return;
          }
        } else {
          // No active session, check localStorage but verify token is still valid
          const storedUser = localStorage.getItem('scanmaster_user');
          const storedToken = localStorage.getItem('scanmaster_token');
          
          if (storedUser && storedToken) {
            try {
              // Try to verify the stored token
              const { data: userData, error: userError } = await supabase.auth.getUser(storedToken);
              
              if (userError || !userData?.user) {
                // Token is invalid, clear storage
                console.warn('Stored token is invalid, clearing:', userError);
                localStorage.removeItem('scanmaster_user');
                localStorage.removeItem('scanmaster_token');
                setUser(null);
              } else {
                // Token is valid, restore user
                const parsedUser = JSON.parse(storedUser);
                setUser(parsedUser);
              }
            } catch (e) {
              // Token verification failed, clear storage
              console.error('Error verifying stored token:', e);
              localStorage.removeItem('scanmaster_user');
              localStorage.removeItem('scanmaster_token');
              setUser(null);
            }
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // Clear potentially invalid data
        localStorage.removeItem('scanmaster_user');
        localStorage.removeItem('scanmaster_token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(getApiUrl('auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        const userData = { email: data.email, name: data.name, token: data.token };
        setUser(userData);
        localStorage.setItem('scanmaster_user', JSON.stringify(userData));
        localStorage.setItem('scanmaster_token', data.token);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const signup = async (name, email, password) => {
    try {
      const response = await fetch(getApiUrl('auth/signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        const userData = { email: data.email, name: data.name, token: data.token };
        setUser(userData);
        localStorage.setItem('scanmaster_user', JSON.stringify(userData));
        localStorage.setItem('scanmaster_token', data.token);
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Signup failed' };
      }
    } catch (error) {
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const loginWithGoogle = async () => {
    try {
      await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
    } catch (error) {
      console.error('Failed to initiate Google login via Supabase:', error);
      return { success: false, error: 'Failed to initiate Google login' };
    }
  };

  const loginWithLinkedIn = async () => {
    try {
      // Ensure LinkedIn OIDC provider is enabled in Supabase before using this
      await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
    } catch (error) {
      console.error('Failed to initiate LinkedIn login via Supabase:', error);
      return { success: false, error: 'Failed to initiate LinkedIn login' };
    }
  };

  const setUserFromToken = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      // Sign out from Supabase (clears any active OAuth/session cookies)
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Error signing out from Supabase:', error);
    } finally {
      // Clear app-level auth state and tokens
      setUser(null);
      localStorage.removeItem('scanmaster_user');
      localStorage.removeItem('scanmaster_token');
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, signup, logout, loading, loginWithGoogle, loginWithLinkedIn, setUserFromToken }}>
      {children}
    </AuthContext.Provider>
  );
};

